<template>
  <va-form
    :id="isEdit ? header.id : ''"
    ref="form"
    :item="header"
    v-model="form"
    :before-save="beforeSave"
    :after-save="afterSave"
    :required-fields-set="requiredFieldsSet"
    :modify-before-send="modifyBeforeSend">
    <v-card class="spacing-bottom--24">
      <div class="d-flex">
        <div class="d-flex flex-column">
          <v-card-title>Details</v-card-title>
          <v-card-subtitle class="spacing-bottom--16">
            {{ $t("resources.headers.headers_details_block_subtitle") }}
          </v-card-subtitle>
        </div>
        <v-card-actions class="ml-auto">
          <v-checkbox
            color="primary"
            v-model="form.isDefault"
            :label="$t('resources.headers.set_as_default')"></v-checkbox>
        </v-card-actions>
      </div>
      <div v-if="form.updatedAt" class="spacing-bottom--16">
        <div class="updated-at-label">
          {{ $t("resources.headers.fields.last_updated") }}
        </div>
        <div class="updated-at-time">
          {{ getFullDateAndTime(form.updatedAt) }}
          {{ form.updatedBy && `by ${form.updatedBy.firstName} ${form.updatedBy.lastName}` }}
        </div>
      </div>
      <va-text-input
        required
        v-model="form.name"
        source="name"
        class="spacing-bottom--16"
        :label="$t('resources.headers.fields.name')"
        :placeholder="$t('resources.headers.headers_name_placeholder')"></va-text-input>

      <va-autocomplete-input
        v-model="form.domain"
        source="domain"
        :disabled="isDomainProvided"
        class="spacing-bottom--16"
        required
        :placeholder="$t('resources.headers.headers_domain_placeholder')"
        :label="$t('resources.headers.domain')"
        :choices="domainsList"
        :min-chars="1"
        cache-items
        @change="domainChanged"></va-autocomplete-input>

      <div class="page-wrapper" v-if="form.pages && form.pages.length > 0">
        <div class="page-label">
          {{ $t("resources.headers.fields.page") }}
        </div>
        <div class="chip-container">
          <v-chip
            color="primary-20"
            text-color="secondary-btn"
            close
            close-icon="mdi-close"
            @click:close="removePage(page)"
            v-for="page in getPageArray"
            :key="page.id">
            {{ getPageName(page) }}
          </v-chip>
          <div v-if="form.pages.length > 12">
            <v-btn
              v-if="!showAllPageChips"
              text
              color="primary"
              class="chip-action-button"
              @click="showAllPageChips = true"
              :ripple="false">
              {{ $t("resources.calendars.show_all") }}
            </v-btn>
            <v-btn
              v-else
              text
              color="primary"
              class="chip-action-button"
              @click="showAllPageChips = false"
              :ripple="false">
              {{ $t("resources.calendars.collapse") }}
            </v-btn>
          </div>
        </div>
      </div>

      <v-select
        v-model="form.template"
        :label="$t('resources.headers.desktop_layout')"
        :items="desktopTemplateOptions"
        @change="headerTemplateChange(form.template)"
        :menu-props="{
          offsetOverflow: true,
          offsetY: true,
        }">
        <template v-slot:append>
          <v-icon>mdi-chevron-down</v-icon>
        </template>
      </v-select>

      <v-select
        v-model="form.mobileTemplate"
        v-if="showMobileLayoutField && form.mobileTemplate"
        :label="$t('resources.headers.mobile_layout')"
        :items="HEADER_MOBILE_TEMPLATES"
        :menu-props="{
          offsetOverflow: true,
          offsetY: true,
        }">
        <template v-slot:append>
          <v-icon>mdi-chevron-down</v-icon>
        </template>
      </v-select>

      <v-select
        v-model="form.logoPosition"
        v-if="showMobileLayoutField && form.logoPosition"
        :label="$t('resources.headers.mobile_layout')"
        :items="HEADER_LOGO_POSITIONS"
        :menu-props="{
          offsetOverflow: true,
          offsetY: true,
        }">
        <template v-slot:append>
          <v-icon>mdi-chevron-down</v-icon>
        </template>
      </v-select>

      <VaImageDragAndDropInput
        class="spacing-bottom--16"
        :item-id="header ? header.logo : ''"
        :label="$t('resources.headers.default_logo')"
        @update:photo="updateHeaderLogo"></VaImageDragAndDropInput>

      <VaImageDragAndDropInput
        class="spacing-bottom--16"
        :item-id="header ? header.mobileLogo : ''"
        :label="$t('resources.headers.mobile_logo')"
        @update:photo="updateHeaderMobileLogo"></VaImageDragAndDropInput>

      <va-tinymce-input
        v-if="showCustomContentField"
        source="customContent"
        :title="$t('resources.headers.content')"
        :current-content="form.customContent"
        :editor-height="320"
        :stripped-down="true"
        :disable-image-filters="true"
        @update="updateContent" />
    </v-card>
    <v-card v-if="showMenuField" class="spacing-bottom--16">
      <v-card-title>{{ $t("resources.headers.header_items_block_title") }}</v-card-title>
      <v-card-subtitle class="spacing-bottom--11">
        {{ $t("resources.headers.header_items_block_subtitle") }}
      </v-card-subtitle>
      <va-array-input
        v-model="form.menu.menuItems"
        v-slot="props"
        source="menu.menuItems"
        :label="false"
        :hide-details="'auto'"
        :add-button-text="$t('generic.add_menu_link')">
        <v-select
          v-model="form.menu.menuItems[props.index].type"
          source="type"
          persistent-placeholder
          :placeholder="$t('resources.headers.headers_menu_item_type_placeholder')"
          :hide-details="'auto'"
          :label="$t('resources.headers.item_type')"
          :items="MENU_ITEM_TYPES"
          @change="changeMenuType(props.index)"
          :menu-props="{
            offsetOverflow: true,
            offsetY: true,
          }">
          <template v-slot:append>
            <v-icon>mdi-chevron-down</v-icon>
          </template>
        </v-select>
        <va-text-input
          v-model="form.menu.menuItems[props.index].text"
          class="spacing-horizontal--16"
          :label="$t('resources.headers.item_title')"
          :placeholder="$t('resources.headers.headers_menu_item_title_placeholder')"
          required
          v-bind="props"
          source="text"></va-text-input>
        <va-autocomplete-input
          v-if="form.menu.menuItems[props.index].type === 3"
          v-model="form.menu.menuItems[props.index].category"
          :label="$t('resources.headers.page_category')"
          source="category"
          :placeholder="$t('resources.headers.headers_menu_page_category_placeholder')"
          required
          :filter="{ ...resourcesFilter }"
          v-bind="props"
          reference="page_categories"
          item-value="@id"
          :min-chars="0"
          :items-per-page="5"
          showLoadMore>
        </va-autocomplete-input>
        <va-text-input
          v-if="form.menu.menuItems[props.index].type === 2"
          v-model="form.menu.menuItems[props.index].link"
          :label="$t('resources.headers.external_link')"
          :placeholder="$t('resources.headers.headers_menu_item_link_placeholder')"
          required
          :rules="[v => !!urlValidation(v) || $t('generic.validation.url')]"
          v-bind="props"
          source="link"></va-text-input>
        <va-autocomplete-input
          v-if="form.menu.menuItems[props.index].type !== 3 && form.menu.menuItems[props.index].type !== 2"
          v-model="form.menu.menuItems[props.index].page"
          v-bind="props"
          :label="$t('resources.headers.existing_page')"
          :placeholder="$t('resources.headers.headers_menu_page_placeholder')"
          :filter="{ ...resourcesFilter, status: [1, 2] }"
          source="page"
          reference="pages"
          item-value="@id"
          required
          searchQuery="headline"
          :min-chars="0"
          :items-per-page="5"
          showLoadMore>
        </va-autocomplete-input>
        <v-select
          class="spacing-left--16"
          v-model="form.menu.menuItems[props.index].action"
          persistent-placeholder
          :menu-props="{ offsetY: true, offsetOverflow: true }"
          :hide-details="'auto'"
          :label="$t('generic.open_in')"
          :items="MENU_TAB_TYPES">
          <template v-slot:append>
            <v-icon>mdi-chevron-down</v-icon>
          </template>
        </v-select>
        <va-number-input
          style="display: none"
          disabled
          :value="(form.menu.menuItems[props.index].position = props.index + 1)"
          required
          v-bind="props"
          source="position"></va-number-input>
      </va-array-input>
    </v-card>

    <v-card>
      <v-card-title>{{ $t("resources.headers.colors_title") }} </v-card-title>
      <v-card-subtitle>{{ $t("resources.headers.colors_subtitle") }}</v-card-subtitle>

      <div class="d-flex">
        <v-card-text>
          <va-color-picker
            @is-hovered="e => (isBackgroundFieldHovered = e)"
            class="mb-4"
            :title="$t('resources.headers.background_color')"
            v-model="form.backgroundColor">
          </va-color-picker>
          <va-color-picker
            @is-hovered="e => (isBorderFieldHovered = e)"
            class="mb-4"
            :title="$t('resources.headers.border_color')"
            v-model="form.borderColor">
          </va-color-picker>
          <div class="d-flex">
            <va-color-picker
              v-if="['blogs', 'blogs_green'].includes(form.template)"
              @is-hovered="e => (isMenuItemTextFieldHovered = e)"
              class="mb-4 mr-4"
              :title="$t('resources.headers.menu_item_color')"
              v-model="form.menuItemColor">
            </va-color-picker>
            <va-color-picker
              v-if="['blogs', 'blogs_green'].includes(form.template)"
              @is-hovered="e => (isMenuItemHoverFieldHovered = e)"
              @is-active="e => (isMenuItemHoverFieldActive = e)"
              class="mb-4"
              :title="$t('resources.headers.hover_color')"
              v-model="form.hoverColor">
            </va-color-picker>
          </div>
          <va-color-picker
            v-if="['blogs', 'blogs_green'].includes(form.template)"
            @is-hovered="e => (isMenuItemBgHoverFieldHovered = e)"
            @is-active="e => (isMenuItemBgHoverFieldActive = e)"
            class="mb-4"
            :title="$t('resources.headers.hover_background_color')"
            v-model="form.hoverBackgroundColor">
          </va-color-picker>
        </v-card-text>

        <v-card-text class="preview-box">
          <label class="preview-box-label"> {{ $t("generic.preview_box.label") }} </label>
          <div class="preview-box-container">
            <div class="preview-box__template">
              <div class="label">{{ $t("generic.preview_box.desktop_layout") }}</div>

              <div
                class="preview-box__template-wrapper"
                :class="{ 'bg-mask': isBackgroundFieldHovered, 'border-mask': isBorderFieldHovered }"
                :style="{ 'background-color': form.backgroundColor, 'border-bottom-color': form.borderColor }">
                <div class="preview-box__template-logo">{{ $t("generic.preview_box.logo") }}</div>
                <div class="preview-box__template-menu">
                  <div
                    class="preview-box__template-menu__item"
                    :class="{
                      'menu-item-mask': isMenuItemTextFieldHovered || isMenuItemHoverFieldHovered,
                    }"
                    :style="{ 'background-color': isMenuItemHoverFieldActive ? form.hoverColor : form.menuItemColor }">
                    <img
                      v-if="isMenuItemHoverFieldHovered || isMenuItemHoverFieldActive"
                      class="preview-box__template-menu__item__cursor"
                      :src="cursorPointerIcon"
                      alt="cursor pointer icon" />
                    <div
                      v-if="isMenuItemBgHoverFieldHovered || isMenuItemBgHoverFieldActive"
                      class="menu-bg-mask"
                      :class="{ hovered: isMenuItemBgHoverFieldHovered }"
                      :style="{ 'background-color': form.hoverBackgroundColor }">
                      <div
                        class="preview-box__template-menu__item preview-box__template-menu__item--active"
                        :style="{
                          'background-color': isMenuItemBgHoverFieldHovered ? form.menuItemColor : form.hoverColor,
                        }">
                        <img
                          class="preview-box__template-menu__item__cursor"
                          :src="cursorPointerIcon"
                          alt="cursor pointer icon" />
                      </div>
                    </div>
                  </div>
                  <div
                    class="preview-box__template-menu__item"
                    :class="{ 'menu-item-mask': isMenuItemTextFieldHovered }"
                    :style="{ 'background-color': form.menuItemColor }"></div>
                  <div
                    class="preview-box__template-menu__item"
                    :class="{ 'menu-item-mask': isMenuItemTextFieldHovered }"
                    :style="{ 'background-color': form.menuItemColor }"></div>
                </div>
              </div>
            </div>

            <div class="preview-box__template preview-box__template--mobile">
              <div class="label">{{ $t("generic.preview_box.mobile_layout") }}</div>

              <div
                class="preview-box__template-wrapper"
                :class="{ 'bg-mask': isBackgroundFieldHovered, 'border-mask': isBorderFieldHovered }"
                :style="{ 'background-color': form.backgroundColor, 'border-bottom-color': form.borderColor }">
                <div class="preview-box__template-logo">{{ $t("generic.preview_box.logo") }}</div>
                <div class="preview-box__template-menu">
                  <img class="preview-box__template-menu__burger" :src="mobileBurgerIcon" alt="mobile burger icon" />
                </div>
              </div>
            </div>
          </div>
          <div class="preview-box-description" v-html="$t('generic.preview_box.description')"></div>
        </v-card-text>
      </div>
    </v-card>

    <required-field-text></required-field-text>
  </va-form>
</template>

<script>
  import definedAbility from "@/ability";
  // Components
  import VaImageDragAndDropInput from "../custom/VaImageDragAndDropInput";
  import VaColorPicker from "@/components/custom/VaColorPicker";
  import RequiredFieldText from "@/components/custom/RequiredFieldText";

  // Mixins
  import ConnectedDomainsList from "@/mixins/connectedDomainsList";
  import crudFormMixin from "@/mixins/crudFormMixin";
  import changeMenuType from "@/mixins/changeMenuTypeMethod";
  import providedDomain from "@/mixins/providedDomain";

  // Constants
  import {
    MENU_ITEM_TYPES,
    HEADER_LOGO_POSITIONS,
    HEADER_TEMPLATES_OLD,
    HEADER_TEMPLATES,
    HEADERS_TEMPLATES_UPDATES_DATE,
    HEADER_MOBILE_TEMPLATES,
    MENU_TAB_TYPES,
  } from "../../constant/index";

  // Utils
  import { convertAssetsPathToAbsoluteLinks, convertAssetsPathToRelativeLinks } from "@/utility/convertAssetsPaths";
  import { parseToHtml } from "@/utility/parseToHtml";
  import { formatMenuItems } from "@/utility/formatMenuItems";
  import { urlValidation } from "@/utility/urlValidation";
  import { removeAlpha } from "@/utility/removeAlpha";
  import { cleanupTinymceContent } from "@/utility/cleanupTinymceContent";
  import { getFullDateAndTime } from "@core/utils/filter";

  const mobileBurgerIcon = require("@/assets/images/svg/mobile-burger.svg");
  const cursorPointerIcon = require("@/assets/images/svg/cursor-pointer.svg");

  export default {
    name: "HeaderForm",
    components: {
      VaImageDragAndDropInput,
      VaColorPicker,
      RequiredFieldText,
    },
    mixins: [crudFormMixin, ConnectedDomainsList, changeMenuType, providedDomain],
    props: {
      header: {
        type: Object,
        default: () => {},
      },
      isEdit: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        urlValidation,
        ability: definedAbility(),
        mobileBurgerIcon,
        cursorPointerIcon,
        HEADER_LOGO_POSITIONS,
        HEADER_TEMPLATES_OLD,
        HEADERS_TEMPLATES_UPDATES_DATE,
        HEADER_TEMPLATES,
        MENU_ITEM_TYPES,
        HEADER_MOBILE_TEMPLATES,
        MENU_TAB_TYPES,
        requiredFormFields: ["name", "domain"],
        form: {
          customContent: "",
        },
        showMobileLayoutField: true,
        showMenuField: true,
        showCustomContentField: true,
        isBackgroundFieldHovered: false,
        isBorderFieldHovered: false,
        isMenuItemTextFieldHovered: false,
        isMenuItemHoverFieldHovered: false,
        isMenuItemHoverFieldActive: false,
        isMenuItemBgHoverFieldHovered: false,
        isMenuItemBgHoverFieldActive: false,
        desktopTemplateOptions: HEADER_TEMPLATES,
        oldDomain: "",
        oldPages: [],
        showAllPageChips: false,
        getFullDateAndTime,
      };
    },
    computed: {
      resourcesFilter() {
        return this.form.domain ? { domain: this.form.domain.replace("/api/domains/", "") } : null;
      },
      getPageArray() {
        if (this.showAllPageChips) {
          return this.form.pages;
        } else {
          return this.form.pages.slice(0, 12);
        }
      },
    },
    beforeMount() {
      const isDuplicateForm = !this.isEdit && !!this.header;

      this.form = this.header ? this.header : {};
      if (!this.isEdit) {
        delete this.form["@context"];
        delete this.form["@id"];
        delete this.form["@type"];
      }

      if (this.header && new Date(this.header.createdAt) < new Date(HEADERS_TEMPLATES_UPDATES_DATE)) {
        this.desktopTemplateOptions = [...HEADER_TEMPLATES, ...HEADER_TEMPLATES_OLD];
      } else {
        this.form.mobileTemplate = this.header?.mobileTemplate || HEADER_MOBILE_TEMPLATES[0].value;
      }

      if (isDuplicateForm) {
        this.form.pages = [];
        this.form.updatedAt = null;
        this.form.updatedBy = null;
      }

      if (!this.ability.can("view", "header blog desktop layouts")) {
        this.desktopTemplateOptions = this.desktopTemplateOptions.filter(item => !item.value.includes("blogs"));
      }

      this.mainContentToShow = this.header?.customContent || "";
      this.form.logo = this.header?.logo?.["@id"] || null;
      this.form.mobileLogo = this.header?.mobileLogo?.["@id"] || null;
      this.form.backgroundColor = removeAlpha(this.header?.backgroundColor) || "#FFFFFF";
      this.form.borderColor = removeAlpha(this.header?.borderColor) || "#FFFFFF";
      this.form.domain = this.header?.domain?.["@id"];
      this.oldDomain = this.header?.domain;
      this.oldPages = this.header?.pages || [];
      this.form.menu = this.header?.menu || {};
      this.form.template = this.header?.template || "logo_left_content";
      this.form.menu.domain = this.form.domain;
      this.form.menuItemColor = removeAlpha(this.header?.menuItemColor) || "#B6B6B6";
      this.form.hoverColor = removeAlpha(this.header?.hoverColor) || "#FFFFFF";
      this.form.hoverBackgroundColor = removeAlpha(this.header?.hoverBackgroundColor) || "#FFFFFF";
      this.form.menu.menuItems = formatMenuItems(this.header?.menu.menuItems);
      if (this.header?.customContent)
        this.updateCustomContent(convertAssetsPathToAbsoluteLinks(this.header.customContent));

      //Check if it is not edit page and not create new page
      if (!this.isEdit && !!this.header) {
        delete this.form.menu["@id"];
        delete this.form.menu.id;
        delete this.form.menu["@type"];

        //This conditional have to be here because menu could be without menuItems
        if (this.form.menu.menuItems) {
          this.form.menu.menuItems.map(item => {
            delete item["@id"];
            delete item["@type"];
          });
        }
      }

      if (["blogs", "blogs_green"].includes(this.form.template)) {
        this.showCustomContentField = false;
        this.showMobileLayoutField = false;
      } else {
        this.showMenuField = false;
        this.form.menu = null;
      }
    },
    methods: {
      getPageName(page) {
        return page.headline.length > 30 ? page.headline.substring(0, 30) + "..." : page.headline;
      },
      removePage(page) {
        this.form.pages = this.form.pages.filter(item => item["@id"] !== page["@id"]);
        this.oldPages = this.form.pages;
      },
      modifyBeforeSend(data) {
        data.pages = data.pages.map(page => page["@id"]);

        return data;
      },
      updateCustomContent(newValue) {
        this.mainContentToShow = newValue;
        let htmlElementFromContent = parseToHtml(this.mainContentToShow);

        htmlElementFromContent.querySelectorAll("img").forEach(e => {
          // If the image is not from external source
          if (e.dataset.image !== "") {
            // eslint-disable-next-line no-self-assign
            e.src = e.src;
          }
        });

        cleanupTinymceContent(htmlElementFromContent);
        this.form.customContent = htmlElementFromContent.body.innerHTML;
      },
      domainChanged(domain) {
        if (this.form.menu) {
          this.form.menu.domain = domain;

          if (this.form.menu.menuItems) {
            this.form.menu.menuItems.forEach(item => {
              item.page = null;
              item.category = null;
            });
          }
        }
        if (this.oldDomain !== domain) {
          this.form.pages = [];
        } else {
          this.form.pages = this.oldPages;
        }
      },
      updateContent(newValue) {
        this.mainContentToShow = newValue;
        let htmlElementFromContent = parseToHtml(this.mainContentToShow);

        htmlElementFromContent.querySelectorAll("img").forEach(e => {
          // If the image is not from external source
          if (e.dataset.image !== "") {
            // eslint-disable-next-line no-self-assign
            e.src = e.src;
          }
        });

        cleanupTinymceContent(htmlElementFromContent);
        this.form.customContent = htmlElementFromContent.body.innerHTML;
      },
      headerTemplateChange(headerTemplate) {
        if (["blogs", "blogs_green"].includes(headerTemplate)) {
          this.form.menu = this.form.menu || {};
          this.form.menu.domain = this.form.menu.domain || this.form.domain;
          this.form.borderColor = "#000000";
          this.showMobileLayoutField = false;
          this.showCustomContentField = false;
          this.showMenuField = true;
        } else {
          this.form.borderColor = "#FFFFFF";
          this.showMobileLayoutField = true;
          this.showCustomContentField = true;
          this.showMenuField = false;
        }

        if (["default", "mobile_burger", "centred_logo"].includes(headerTemplate)) {
          this.form.logoPosition = HEADER_LOGO_POSITIONS[0].value;
          this.form.mobileTemplate = null;
        } else {
          this.form.mobileTemplate = HEADER_MOBILE_TEMPLATES[0].value;
          this.form.logoPosition = null;
        }
      },
      updateHeaderLogo(newValue) {
        if (newValue === "") {
          this.form.logo = null;
        } else {
          this.form.logo = newValue["@id"];
        }
      },
      updateHeaderMobileLogo(newValue) {
        if (newValue === "") {
          this.form.mobileLogo = null;
        } else {
          this.form.mobileLogo = newValue["@id"];
        }
      },

      submit() {
        if (this.form.customContent) {
          // If for is not empty prepare the shortcode image URLS, to be stripped
          // This functions strips absolute URL's from the image src attribute
          let htmlElementFromContent = convertAssetsPathToRelativeLinks(this.form.customContent);

          cleanupTinymceContent(htmlElementFromContent);

          this.form.customContent = htmlElementFromContent.body.innerHTML;
        }

        if (!["blogs", "blogs_green"].includes(this.form?.template)) {
          this.form.menu = null;
        } else if (this.form?.menu?.["@id"]) {
          // menu.domain value should be IRI
          this.form.menu.domain = this.form.domain;
        }

        this.$refs.form.onSubmit();
      },
    },
    watch: {
      "form.menu.menuItems": {
        handler: function (newVal) {
          newVal.map(item => {
            if (!item.action) {
              item.action = MENU_TAB_TYPES[0].value;
            }
          });
        },
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import "../../@core/preset/preset/functions.scss";
  @import "../../@core/preset/preset/typography/index.scss";

  .page-label {
    @include text-caption-bold();
    color: get-color("subtitle");
    margin-bottom: 12px;
  }

  .page-wrapper {
    margin-bottom: 12px;
  }

  .page-chip-group {
    ::v-deep {
      .v-slide-group__content {
        padding: 0;
      }
    }
  }

  .chip-container {
    display: flex;
    flex-wrap: wrap;
    column-gap: 8px;
    row-gap: 6px;
    align-items: center;

    .chip-action-button.v-btn {
      padding: 5px 10px !important;
      height: unset !important;
      min-width: unset;
      color: get-color("secondary-btn") !important;
    }
  }

  .updated-at-label {
    @include text-caption-bold();
    color: get-color("subtitle");
  }

  .updated-at-time {
    @include text-body-2();
    color: get-color("subtitle");
  }
</style>
